import { Fragment, useState } from "react";
import { format } from "date-fns";
import { ArrowDown, ArrowUp } from "lucide-react";

import { AddTaskModal } from "components/AddTaskModal/AddTaskModal";
import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
import { Dialog } from "components/ui/Dialog";
import { SmsModal2 } from "features/comunications/components/SmsModal2/SmsModal2";
import { ResultHl7Preview } from "features/results/components/ResultHl7Preview/ResultHl7Preview";
import { useResultHl7Document } from "features/results/hooks/useResultHl7Document";
import { useResultsHL7OrderDetails } from "features/results/hooks/useResultsHL7OrderDetails";

import { HL7DetailsTab } from "../HL7DetailsTab/HL7DetailsTab";
import { XmlXsltPreview } from "../XmlXsltPreview/XmlXsltPreview";
import checkboxIcon from "./checkbox-icon.svg";
import styles from "./ResultDetails.module.css";

const getInitialAssigneeComments = (item, selectedItems) => {
  const patientName = item.patient
    ? `${item.patient.firstName} ${item.patient.surname}`
    : item.patientInformationExternal?.fullName || "Unknown Patient";

  return `
${patientName}  - ${format(
    new Date(item.orderDate.split(" ")[0]),
    "dd-MM-yyyy"
  )}

${item?.results
  ?.filter((result) => selectedItems.includes(result.id))
  ?.map((result) =>
    `${result.testName} - ${result.value} - ${result.warning}`.trim()
  )
  .join("\n")}
`.trim();
};

const formatGender = (gender) => {
  switch (gender?.toUpperCase()) {
    case "F":
      return "Female";
    case "M":
      return "Male";
    default:
      return gender || "-";
  }
};

// Parses HealthLink's `YYYYMMDD`, not an ISO date — a different job from the shared
// helper in utils/dateFormatter, and named for it so the two are not read as the same
// function. An identically-named local shadow is what produced KIN-1716's misdiagnosis
// in the vaccine form.
const formatHl7DateOfBirth = (dateString) => {
  if (!dateString || dateString.length !== 8) return "-";

  const year = dateString.substring(0, 4);
  const month = dateString.substring(4, 6);
  const day = dateString.substring(6, 8);

  try {
    const date = new Date(year, month - 1, day);
    return format(date, "dd-MM-yyyy");
  } catch (error) {
    return dateString;
  }
};

const HealthlinkPatientInfo = ({ healthlinkPatient }) => {
  return (
    <div className={styles.healthlinkPatientInfo}>
      <h3>Healthlink Patient Information</h3>
      <div className={styles.patientInfoGrid}>
        <div className={styles.patientInfoItem}>
          <label>Full Name:</label>
          <span>{healthlinkPatient.fullName || "-"}</span>
        </div>
        <div className={styles.patientInfoItem}>
          <label>First Name:</label>
          <span>{healthlinkPatient.firstName || "-"}</span>
        </div>
        <div className={styles.patientInfoItem}>
          <label>Last Name:</label>
          <span>{healthlinkPatient.lastName || "-"}</span>
        </div>
        <div className={styles.patientInfoItem}>
          <label>Patient ID:</label>
          <span>{healthlinkPatient.patientId || "-"}</span>
        </div>
        <div className={styles.patientInfoItem}>
          <label>Gender:</label>
          <span>{formatGender(healthlinkPatient.gender)}</span>
        </div>
        <div className={styles.patientInfoItem}>
          <label>Date of Birth:</label>
          <span>{formatHl7DateOfBirth(healthlinkPatient.dateOfBirth)}</span>
        </div>
        <div className={styles.patientInfoItem}>
          <label>Address:</label>
          <span>{healthlinkPatient.address || "-"}</span>
        </div>
        <div className={styles.patientInfoItem}>
          <label>City:</label>
          <span>{healthlinkPatient.city || "-"}</span>
        </div>
        <div className={styles.patientInfoItem}>
          <label>ID Type:</label>
          <span>{healthlinkPatient.idType || "-"}</span>
        </div>
        <div className={styles.patientInfoItem}>
          <label>Assigning Authority:</label>
          <span>{healthlinkPatient.assigningAuthority || "-"}</span>
        </div>
      </div>
    </div>
  );
};

// Some HL7 result values embed a literal "\.br\" as a line break.
const LINE_BREAK_TOKEN = /\\\.br\\/g;

const ResultItem = ({ value, warning }) => {
  const warningUpper = warning?.toUpperCase();
  const valueUpper = value?.toUpperCase();
  const lines =
    typeof value === "string" && value.length > 0
      ? value.split(LINE_BREAK_TOKEN)
      : null;

  return (
    <span
      className={`${styles.boldItem} ${
        warningUpper === "LOW" ? styles.low : ""
      } ${warningUpper === "HIGH" ? styles.high : ""} ${
        valueUpper === "NEW" ? styles.open : ""
      }`}
    >
      {lines
        ? lines.map((line, index) => (
            <Fragment key={index}>
              {index > 0 && <br />}
              {line}
            </Fragment>
          ))
        : value ?? "-"}
      {warningUpper === "LOW" && (
        <ArrowDown
          style={{ width: "20px", height: "20px", strokeWidth: "2" }}
        />
      )}
      {warningUpper === "HIGH" && (
        <ArrowUp style={{ width: "20px", height: "20px", strokeWidth: "2" }} />
      )}
    </span>
  );
};

export const ResultDetails = ({ item, staffMember, healthlinkPatient }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSmsModalOpen, setIsSmsModalOpen] = useState(false);

  /*
   * The message document for this row: the list's own `rawXml` while the list still
   * sends it, otherwise fetched by control id when the row is opened (KIN-2120). Either
   * way, a row with a document of its own skips the structured detail fetch below.
   */
  const hl7Document = useResultHl7Document(item);

  const { data: hl7Details, isLoading: isHl7Loading } =
    useResultsHL7OrderDetails(item.id, {
      enabled: !hl7Document.hasPreview && !hl7Document.isLoading,
    });

  return (
    <>
      {/* Test results first. */}
      <div className={styles.vaccineDetailsWrapper}>
        {healthlinkPatient && (
          <HealthlinkPatientInfo healthlinkPatient={healthlinkPatient} />
        )}
        {!!item.results?.length && (
          <div className={styles.tablewrapper}>
            <div className={styles.table}>
              <div
                className={`${styles.tableHeader} ${
                  !item.patient && styles.unmatchedHeader
                }`}
              >
                {item.patient && (
                  <div>
                    <img
                      className={styles.menuIcon}
                      alt="checkbox"
                      src={checkboxIcon}
                    />
                  </div>
                )}
                <p id="results-table-test">Test</p>
                <p id="results-table-recent-test">Recent test</p>
                <p id="results-table-recent-result">Recent result</p>
                <p id="results-table-reference-range">Reference range</p>
                <p id="results-table-previous-result">Previous result</p>
              </div>
              {selectedItems?.length > 0 && (
                <div className={styles.tableSelectedItemsWrapper}>
                  <div>
                    <p>{selectedItems?.length} items selected</p>
                    <button onClick={() => setSelectedItems([])}>Clear</button>
                  </div>
                  <div style={{ display: "flex", gap: "24px" }}>
                    <AddTaskModal
                      patient={item.patient}
                      buttonClassName={styles.addTaskButton}
                      buttonText="Add task"
                      staffMemberId={staffMember.staffMember.id?.toString()}
                      initialAssigneeComments={getInitialAssigneeComments(
                        item,
                        selectedItems
                      )}
                    />
                    <button onClick={() => setIsSmsModalOpen(true)}>
                      Send SMS
                    </button>
                  </div>
                </div>
              )}
              <ul className={styles.tableItems}>
                {item?.results?.map((resultItem) => (
                  <li
                    className={`${styles.tableItem} ${
                      !item.patient && styles.unmatchedItem
                    }`}
                  >
                    {item.patient && (
                      <CustomCheckbox
                        checked={selectedItems.includes(resultItem.id)}
                        handleChange={(e) => {
                          if (e.target.checked) {
                            setSelectedItems([...selectedItems, resultItem.id]);
                          } else {
                            setSelectedItems(
                              selectedItems.filter((id) => id !== resultItem.id)
                            );
                          }
                        }}
                      />
                    )}
                    <p aria-describedby="results-table-test">
                      {resultItem.testName}
                    </p>
                    <p aria-describedby="results-table-recent-test">
                      {format(
                        new Date(resultItem.date.split(" ")?.at(0)),
                        "dd-MM-yyyy"
                      )}
                    </p>
                    <p aria-describedby="results-table-recent-result">
                      <ResultItem
                        value={`${resultItem.value} ${resultItem.units}`}
                        warning={resultItem.warning}
                      />
                    </p>
                    <p aria-describedby="results-table-reference-range">
                      {resultItem.lowerLimit && resultItem.upperLimit ? (
                        <>
                          {resultItem.lowerLimit}-{resultItem.upperLimit}{" "}
                          {resultItem.units}
                        </>
                      ) : (
                        "-"
                      )}
                    </p>
                    <p
                      aria-describedby="results-table-previous-result"
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <ResultItem
                        value={resultItem.previousResult?.at(0)?.value}
                        warning={resultItem.previousResult?.at(0)?.warning}
                      />
                      {resultItem.previousResult
                        ?.at(0)
                        ?.date.split(" ")
                        ?.at(0) && (
                        <span style={{ marginLeft: "8px" }}>
                          (
                          {format(
                            new Date(
                              resultItem.previousResult
                                ?.at(0)
                                ?.date.split(" ")
                                ?.at(0)
                            ),
                            "dd-MM-yyyy"
                          ) ?? "-"}
                          )
                        </span>
                      )}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
      {/* HL7 document below the results. */}
      <div className={styles.hl7PreviewSection}>
        <ResultHl7Preview
          document={hl7Document}
          documentKind={item.resultType}
          fallback={
            <XmlXsltPreview
              messageId={item.messageId}
              messageType="lab"
              fallback={
                <HL7DetailsTab loading={isHl7Loading} hl7Details={hl7Details} />
              }
            />
          }
        />
      </div>
      <Dialog
        open={isSmsModalOpen}
        onClose={() => setIsSmsModalOpen(false)}
        title="Send sms"
        width={1000}
      >
        <SmsModal2
          tenantVal={staffMember.tenant}
          patient={item.patient}
          onClose={() => setIsSmsModalOpen(false)}
        />
      </Dialog>
    </>
  );
};
